<template>
  <div>
    <base-material-card
      icon="mdi-history"
      title="ประวัติการเบิกจ่ายงบประมาณพนักงานราชการ"
      class="px-5 py-3"
    >
      <!-- Filters -->
      <v-row class="mb-4" dense>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterYear"
            :items="availableYears"
            label="ปีงบประมาณ"
            outlined
            dense
            clearable
            @change="loadBudgetHistory"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filterTime"
            :items="periodOptions"
            label="งวดรายงาน"
            outlined
            dense
            clearable
            @change="loadBudgetHistory"
          />
        </v-col>
        <v-col cols="12" md="3" class="text-center">
          <v-btn
            color="primary"
            outlined
            @click="loadBudgetHistory"
            :loading="isLoading"
          >
            <v-icon class="mr-1">mdi-magnify</v-icon>
            ค้นหา
          </v-btn>         
        </v-col>
        <v-col cols="12" md="3" class="text-center">          
          <v-btn
            color="success"
            outlined          
            @click="exportToExcel"
            :disabled="groupedData.length === 0"
            :loading="isExporting"
          >
            <v-icon class="mr-1">mdi-file-excel</v-icon>
            ส่งออก EXCEL
          </v-btn>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <!-- No Data -->
      <v-alert
        v-if="!isLoading && groupedData.length === 0"
        type="info"
        outlined
      >
        ไม่พบข้อมูลประวัติการเบิกจ่าย
      </v-alert>

      <!-- ========== Overall Statistics Dashboard ========== -->
      <div v-if="!isLoading && groupedData.length > 0">
        <v-card class="mb-6 elevation-2 stat-dashboard" outlined>
          <v-card-title class="pa-4 stat-header white--text">
            <v-icon color="white" class="mr-2">mdi-chart-box</v-icon>
            สรุปภาพรวมทั้งหมด
            <v-spacer />
            <v-chip color="white" text-color="primary" small>
              {{ groupedData.length }} งวด
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-4">
            <!-- Main KPI Cards -->
            <v-row dense>
              <v-col cols="6" md="3">
                <v-card flat class="pa-3 text-center kpi-card kpi-allocated">
                  <v-icon color="#455a64" class="mb-1">mdi-cash-multiple</v-icon>
                  <div class="text-caption text-grey">งบจัดสรรรวม</div>
                  <div class="text-h6 font-weight-bold" style="color: #455a64">
                    ฿{{ formatCurrency(overallStats.totalAllocated) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card flat class="pa-3 text-center kpi-card kpi-disbursed">
                  <v-icon color="#2e7d32" class="mb-1">mdi-cash-check</v-icon>
                  <div class="text-caption text-grey">เบิกจ่ายรวม</div>
                  <div class="text-h6 font-weight-bold" style="color: #2e7d32">
                    ฿{{ formatCurrency(overallStats.totalDisbursed) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card flat class="pa-3 text-center kpi-card kpi-remaining">
                  <v-icon color="#a86400" class="mb-1">mdi-cash-minus</v-icon>
                  <div class="text-caption text-grey">คงเหลือรวม</div>
                  <div class="text-h6 font-weight-bold" style="color: #a86400">
                    ฿{{ formatCurrency(overallStats.totalRemaining) }}
                  </div>
                </v-card>
              </v-col>
              <v-col cols="6" md="3">
                <v-card flat class="pa-3 text-center kpi-card kpi-rate">
                  <v-icon color="#455a64" class="mb-1">mdi-percent-outline</v-icon>
                  <div class="text-caption text-grey">อัตราเบิกจ่ายเฉลี่ย</div>
                  <div class="text-h6 font-weight-bold" style="color: #455a64">
                    {{ overallStats.avgPercentage }}%
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Progress bar -->
            <v-progress-linear
              :value="overallStats.avgPercentage"
              height="10"
              rounded
              class="mt-4 mb-3"
              :color="getPercentageColor(overallStats.avgPercentage)"
            />
            <div class="d-flex justify-space-between text-caption text-grey">
              <span>0%</span>
              <span>เบิกจ่ายแล้ว {{ overallStats.avgPercentage }}% ของงบทั้งหมด</span>
              <span>100%</span>
            </div>

            <v-divider class="my-4" />

            <!-- Secondary Stats Row -->
            <v-row dense>
              <v-col cols="6" md="3">
                <div class="d-flex align-center">
                  <v-avatar size="36" color="info lighten-4" class="mr-2">
                    <v-icon small color="info">mdi-calendar-range</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption text-grey">จำนวนเดือนทั้งหมด</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ overallStats.totalMonths }} เดือน
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="d-flex align-center">
                  <v-avatar size="36" color="success lighten-4" class="mr-2">
                    <v-icon small color="success">mdi-trending-up</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption text-grey">เดือนที่เบิกจ่ายครบ</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ overallStats.monthsFull }} เดือน
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="d-flex align-center">
                  <v-avatar size="36" color="warning lighten-4" class="mr-2">
                    <v-icon small color="warning">mdi-cash-register</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption text-grey">เบิกจ่ายเฉลี่ย/เดือน</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      ฿{{ formatCurrency(overallStats.avgPerMonth) }}
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="d-flex align-center">
                  <v-avatar size="36" color="error lighten-4" class="mr-2">
                    <v-icon small color="error">mdi-alert-circle</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption text-grey">เดือนที่เบิกจ่ายเกิน</div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ overallStats.monthsOver }} เดือน
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ========== Grouped by Report ========== -->
        <div v-for="group in groupedData" :key="group.report_id" class="mb-6">
          <v-card outlined>
            <!-- Report Header -->
            <v-card-title class="pa-4 grey lighten-4">
              <v-row align="center" no-gutters>
                <v-col>
                  <div class="text-subtitle-1 font-weight-bold">
                    <v-icon small class="mr-1">mdi-file-document</v-icon>
                    {{ group.reporting_period }}
                    — ปี {{ group.report_year }}
                  </div>
                  <div class="text-body-2 text-grey mt-1">
                    {{ group.college_name }}
                    | ผอ. {{ group.director_name }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-chip
                    :color="getStatusColor(group.status)"
                    text-color="white"
                    small
                  >
                    {{ getStatusText(group.status) }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider />

            <!-- Summary Cards -->
            <v-card-text class="pa-4">
              <v-row dense class="mb-4">
                <v-col cols="6" md="3">
                  <div class="text-caption text-grey">งบจัดสรรทั้งงวด</div>
                  <div class="text-h6 font-weight-bold text-primary">
                    ฿{{ formatCurrency(group.total_allocated) }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-caption text-grey">เบิกจ่ายทั้งงวด</div>
                  <div class="text-h6 font-weight-bold text-green">
                    ฿{{ formatCurrency(group.total_disbursed) }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-caption text-grey">คงเหลือทั้งงวด</div>
                  <div
                    class="text-h6 font-weight-bold"
                    :class="group.total_remaining >= 0 ? 'text-orange' : 'text-red'"
                  >
                    ฿{{ formatCurrency(group.total_remaining) }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-caption text-grey">อัตราเบิกจ่าย</div>
                  <div class="text-h6 font-weight-bold text-info">
                    {{ calcPercentage(group.total_disbursed, group.total_allocated) }}%
                  </div>
                </v-col>
              </v-row>

              <v-progress-linear
                :value="calcPercentage(group.total_disbursed, group.total_allocated)"
                height="8"
                rounded
                :color="getPercentageColor(calcPercentage(group.total_disbursed, group.total_allocated))"
                class="mb-4"
              />

              <!-- Monthly Breakdown Table -->
              <v-simple-table dense>
                <thead>
                  <tr class="grey lighten-3">
                    <th>เดือน</th>
                    <th class="text-right">จัดสรร (฿)</th>
                    <th class="text-right">เบิกจ่าย (฿)</th>
                    <th class="text-right">คงเหลือ (฿)</th>
                    <th class="text-right">อัตรา (%)</th>
                    <th>หมายเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="month in group.months" :key="month.id">
                    <td>
                      <v-icon x-small class="mr-1">mdi-calendar-month</v-icon>
                      {{ getMonthName(month.month) }}
                    </td>
                    <td class="text-right">
                      {{ formatCurrency(month.allocated_budget) }}
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ formatCurrency(month.disbursed_budget) }}
                    </td>
                    <td
                      class="text-right font-weight-bold"
                      :class="month.remaining_budget < 0 ? 'text-red' : ''"
                    >
                      {{ formatCurrency(month.remaining_budget) }}
                    </td>
                    <td class="text-right">
                      <v-chip
                        x-small
                        :color="getPercentageColor(calcPercentage(month.disbursed_budget, month.allocated_budget))"
                        text-color="white"
                      >
                        {{ calcPercentage(month.disbursed_budget, month.allocated_budget) }}%
                      </v-chip>
                    </td>
                    <td class="text-body-2 text-grey">
                      {{ month.notes || "-" }}
                    </td>
                  </tr>
                </tbody>
                <!-- Period Summary Footer -->
                <tfoot>
                  <tr class="grey lighten-4 font-weight-bold">
                    <td>รวมทั้งงวด</td>
                    <td class="text-right">
                      {{ formatCurrency(group.total_allocated) }}
                    </td>
                    <td class="text-right text-green">
                      {{ formatCurrency(group.total_disbursed) }}
                    </td>
                    <td
                      class="text-right"
                      :class="group.total_remaining < 0 ? 'text-red' : 'text-orange'"
                    >
                      {{ formatCurrency(group.total_remaining) }}
                    </td>
                    <td class="text-right">
                      <v-chip
                        small
                        :color="getPercentageColor(calcPercentage(group.total_disbursed, group.total_allocated))"
                        text-color="white"
                      >
                        {{ calcPercentage(group.total_disbursed, group.total_allocated) }}%
                      </v-chip>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </base-material-card>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  props: {
    user: { type: [Array, Object] },
    period_college: { type: [Array, Object] }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      isLoading: false,
      isExporting: false,
      filterYear: null,
      filterTime: null,
      budgetData: [],
      availableYears: [],
      periodOptions: [
        { text: "งวดที่ 1", value: 1 },
        { text: "งวดที่ 2", value: 2 },
        { text: "งวดที่ 3", value: 3 },
        { text: "งวดที่ 4", value: 4 }
      ],
      monthNames: [
        "",
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม"
      ]
    };
  },

  computed: {
    groupedData() {
      const map = {};
      for (const row of this.budgetData) {
        const rid = row.report_id;
        if (!map[rid]) {
          map[rid] = {
            report_id: rid,
            college_name: row.college_name,
            report_year: row.report_year,
            report_time: row.report_time,
            status: row.status,
            reporting_period: row.reporting_period,
            director_name: row.director_name,
            total_allocated: parseFloat(row.total_allocated) || 0,
            total_disbursed: parseFloat(row.total_disbursed) || 0,
            total_remaining: parseFloat(row.total_remaining) || 0,
            months: []
          };
        }
        map[rid].months.push(row);
      }
      return Object.values(map);
    },

    overallStats() {
      let totalAllocated = 0;
      let totalDisbursed = 0;
      let totalRemaining = 0;
      let totalMonths = 0;
      let monthsFull = 0;
      let monthsOver = 0;

      for (const group of this.groupedData) {
        totalAllocated += group.total_allocated;
        totalDisbursed += group.total_disbursed;
        totalRemaining += group.total_remaining;

        for (const m of group.months) {
          totalMonths++;
          const alloc = parseFloat(m.allocated_budget) || 0;
          const disb = parseFloat(m.disbursed_budget) || 0;
          const rem = parseFloat(m.remaining_budget) || 0;
          if (alloc > 0 && disb >= alloc) monthsFull++;
          if (rem < 0) monthsOver++;
        }
      }

      const avgPercentage =
        totalAllocated > 0
          ? Math.round((totalDisbursed / totalAllocated) * 100)
          : 0;
      const avgPerMonth = totalMonths > 0 ? totalDisbursed / totalMonths : 0;

      return {
        totalAllocated,
        totalDisbursed,
        totalRemaining,
        avgPercentage,
        totalMonths,
        monthsFull,
        monthsOver,
        avgPerMonth
      };
    }
  },

  async mounted() {
    this.initYears();
  /*   this.filterYear = this.period_college.period_college_year || null;
    this.filterTime = this.period_college.period_college_times || null; */
    await this.loadBudgetHistory();
  },

  methods: {
    initYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.availableYears = [];
      for (let i = 0; i <= 5; i++) {
        this.availableYears.push(currentYear - i);
      }
    },

    async loadBudgetHistory() {
      this.isLoading = true;
      try {
        const payload = {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code || ""
        };
        if (this.filterYear) payload.report_year = this.filterYear;
        if (this.filterTime) payload.report_time = this.filterTime;
        const response = await this.$http.post(
          "government_employee_report_monthly_budget.read.php",
          payload
        );
        this.budgetData = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Error loading budget history:", error);
        this.budgetData = [];
      } finally {
        this.isLoading = false;
      }
    },

    /* ===== Excel Export ===== */
    exportToExcel() {
      if (this.groupedData.length === 0) return;
      this.isExporting = true;

      try {
        const wb = XLSX.utils.book_new();
        this.buildSummarySheet(wb);
        this.groupedData.forEach((group, idx) => {
          this.buildGroupSheet(wb, group, idx);
        });

        const yearLabel = this.filterYear || "ทั้งหมด";
        const timeLabel = this.filterTime ? `งวด${this.filterTime}` : "ทุกงวด";
        XLSX.writeFile(wb, `เบิกจ่ายงบประมาณ_${yearLabel}_${timeLabel}.xlsx`);
      } catch (error) {
        console.error("Error exporting Excel:", error);
        alert("เกิดข้อผิดพลาดในการส่งออก Excel");
      } finally {
        this.isExporting = false;
      }
    },

    buildSummarySheet(wb) {
      const s = this.overallStats;
      const rows = [];

      rows.push(["รายงานสรุปภาพรวมการเบิกจ่ายงบประมาณพนักงานราชการ"]);
      rows.push([
        `วันที่ส่งออก: ${new Date().toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric"
        })}`
      ]);
      rows.push([]);

      rows.push(["ตัวชี้วัดหลัก (KPI)"]);
      rows.push(["รายการ", "จำนวน", "หน่วย"]);
      rows.push(["งบจัดสรรรวมทั้งหมด", s.totalAllocated, "บาท"]);
      rows.push(["เบิกจ่ายรวมทั้งหมด", s.totalDisbursed, "บาท"]);
      rows.push(["คงเหลือรวมทั้งหมด", s.totalRemaining, "บาท"]);
      rows.push(["อัตราเบิกจ่ายเฉลี่ย", s.avgPercentage / 100]);
      rows.push(["เบิกจ่ายเฉลี่ยต่อเดือน", s.avgPerMonth, "บาท"]);
      rows.push([]);

      rows.push(["สถิติเพิ่มเติม"]);
      rows.push(["จำนวนงวดรายงาน", this.groupedData.length, "งวด"]);
      rows.push(["จำนวนเดือนทั้งหมด", s.totalMonths, "เดือน"]);
      rows.push(["เดือนที่เบิกจ่ายครบ 100%", s.monthsFull, "เดือน"]);
      rows.push(["เดือนที่เบิกจ่ายเกินงบ", s.monthsOver, "เดือน"]);
      rows.push([]);

      rows.push(["เปรียบเทียบรายงวด"]);
      rows.push([
        "งวดรายงาน",
        "ปี",
        "จัดสรร (บาท)",
        "เบิกจ่าย (บาท)",
        "คงเหลือ (บาท)",
        "อัตรา (%)",
        "สถานะ"
      ]);
      this.groupedData.forEach((g) => {
        rows.push([
          g.reporting_period,
          String(g.report_year),
          g.total_allocated,
          g.total_disbursed,
          g.total_remaining,
          this.calcPercentage(g.total_disbursed, g.total_allocated) / 100,
          this.getStatusText(g.status)
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 30 }, { wch: 18 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 16 }
      ];
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } }
      ];

      const cFmt = "#,##0.00";
      const pFmt = "0%";

      [5, 6, 7].forEach((r) => {
        const ref = XLSX.utils.encode_cell({ r, c: 1 });
        if (ws[ref]) ws[ref].z = cFmt;
      });
      this.setCellFmt(ws, 8, 1, pFmt);
      this.setCellFmt(ws, 9, 1, cFmt);

      const compStart = 18;
      this.groupedData.forEach((_, i) => {
        const r = compStart + i;
        [2, 3, 4].forEach((c) => this.setCellFmt(ws, r, c, cFmt));
        this.setCellFmt(ws, r, 5, pFmt);
      });

      XLSX.utils.book_append_sheet(wb, ws, "สรุปภาพรวม");
    },

    buildGroupSheet(wb, group, idx) {
      const rows = [];

      rows.push(["รายละเอียดการเบิกจ่ายงบประมาณ"]);
      rows.push([]);
      rows.push(["สถานศึกษา", group.college_name]);
      rows.push(["ผู้อำนวยการ", group.director_name]);
      rows.push(["งวดรายงาน", group.reporting_period]);
      rows.push(["ปีงบประมาณ", String(group.report_year)]);
      rows.push(["สถานะ", this.getStatusText(group.status)]);
      rows.push([]);

      rows.push(["สรุปงวด"]);
      rows.push(["งบจัดสรรทั้งงวด", group.total_allocated, "บาท"]);
      rows.push(["เบิกจ่ายทั้งงวด", group.total_disbursed, "บาท"]);
      rows.push(["คงเหลือทั้งงวด", group.total_remaining, "บาท"]);
      rows.push([
        "อัตราเบิกจ่าย",
        this.calcPercentage(group.total_disbursed, group.total_allocated) / 100
      ]);
      rows.push([]);

      rows.push([
        "เดือน", "จัดสรร (บาท)", "เบิกจ่าย (บาท)",
        "คงเหลือ (บาท)", "อัตรา (%)", "หมายเหตุ"
      ]);

      const dataStart = rows.length;
      group.months.forEach((m) => {
        rows.push([
          this.getMonthName(m.month),
          parseFloat(m.allocated_budget) || 0,
          parseFloat(m.disbursed_budget) || 0,
          parseFloat(m.remaining_budget) || 0,
          this.calcPercentage(m.disbursed_budget, m.allocated_budget) / 100,
          m.notes || "-"
        ]);
      });

      const totalRow = rows.length;
      rows.push([
        "รวมทั้งงวด",
        group.total_allocated,
        group.total_disbursed,
        group.total_remaining,
        this.calcPercentage(group.total_disbursed, group.total_allocated) / 100,
        ""
      ]);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws["!cols"] = [
        { wch: 20 }, { wch: 18 }, { wch: 18 },
        { wch: 18 }, { wch: 14 }, { wch: 25 }
      ];
      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

      const cFmt = "#,##0.00";
      const pFmt = "0%";

      [9, 10, 11].forEach((r) => this.setCellFmt(ws, r, 1, cFmt));
      this.setCellFmt(ws, 12, 1, pFmt);

      [...group.months.map((_, i) => dataStart + i), totalRow].forEach((r) => {
        [1, 2, 3].forEach((c) => this.setCellFmt(ws, r, c, cFmt));
        this.setCellFmt(ws, r, 4, pFmt);
      });

      let sheetName = (group.reporting_period || `งวด_${idx + 1}`)
        .substring(0, 28)
        .replace(/[\\/*?:\[\]]/g, "");

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    },

    setCellFmt(ws, r, c, fmt) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (ws[ref]) ws[ref].z = fmt;
    },

    /* ===== Helpers ===== */
    getMonthName(val) {
      return this.monthNames[parseInt(val)] || `เดือน ${val}`;
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    calcPercentage(disbursed, allocated) {
      const a = parseFloat(allocated) || 0;
      if (a === 0) return 0;
      return Math.round((parseFloat(disbursed || 0) / a) * 100);
    },

    getPercentageColor(pct) {
      if (pct >= 80) return "green";
      if (pct >= 50) return "orange";
      return "red";
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        submitted: "blue",
        approved: "green",
        rejected: "red",
        cancelled: "orange",
        checking: "primary"
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        draft: "ร่าง",
        submitted: "รอการอนุมัติ",
        approved: "อนุมัติแล้ว",
        rejected: "ปฏิเสธ",
        cancelled: "ยกเลิก",
        checking: "กำลังตรวจสอบ"
      };
      return texts[status] || status;
    }
  }
};
</script>

<style scoped>
.text-green {
  color: #2e7d32;
}
.text-orange {
  color: #a86400;
}
.text-red {
  color: #f44336;
}
.text-info {
  color: #455a64;
}
.text-primary {
  color: #455a64;
}
.text-grey {
  color: #757575;
}

.stat-dashboard {
  border: none;
  border-radius: 12px;
  overflow: hidden;
}
.stat-header {
  background: linear-gradient(135deg, #455a64 0%, #455a64 100%);
}
.kpi-card {
  border-radius: 10px;
  transition: transform 0.2s, box-shadow 0.2s;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.kpi-allocated {
  background: #f5f5f5;
}
.kpi-disbursed {
  background: #e8f5e9;
}
.kpi-remaining {
  background: #fff4dc;
}
.kpi-rate {
  background: #e4e4e7;
}
</style>